import {
  AUDIO_OPTIONS,
  CAMERA_OPTIONS,
  DIALOGUE_OPTIONS,
  GRAPHICS_OPTIONS,
  IMAGE_COMPOSITION_OPTIONS,
  IMAGE_LIGHTING_OPTIONS,
  IMAGE_MOOD_OPTIONS,
  IMAGE_STYLE_OPTIONS,
  LIGHTING_OPTIONS,
  MODEL_CONFIG,
  MOTION_OPTIONS,
  PRESET_PROMPTS,
} from '@/hooks/generator-constants';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSettingsProps {
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
  setContentInput: (value: string | ((prev: string) => string)) => void;
  defaultModel?: string;
}

export function useSettings({
  isAuthenticated,
  isAuthInitialized,
  setContentInput,
  defaultModel,
}: UseSettingsProps) {
  const [model, setModel] = useState(defaultModel || 'sora-2');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('720p');
  const [duration, setDuration] = useState('10s');
  const [variations, setVariations] = useState('1');
  const [mode, setMode] = useState('normal');
  const [outputFormat, setOutputFormat] = useState('jpg');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [autoSwitchEnabled, setAutoSwitchEnabled] = useState(true);

  // Reset UI state on logout (Settings portion)
  useEffect(() => {
    if (isAuthInitialized && !isAuthenticated) {
      setSelectedImage(null);
      setSelectedImages([]);
      setModel(defaultModel || 'sora-2');
      setAspectRatio('16:9');
      setResolution('720p');
      setDuration('10s');
      setVariations('1');
      setMode('normal');
    }
  }, [isAuthenticated, isAuthInitialized, defaultModel]);

  // Persist state across page refreshes
  const hasInitialModelSetRef = useRef(false);
  useEffect(() => {
    if (hasInitialModelSetRef.current) return;
    // If we are on a landing page with a defaultModel, prioritize it on mount
    if (defaultModel) {
      setModel(defaultModel);
    } else {
      const savedModel = localStorage.getItem('sora_generator_model');
      if (savedModel) setModel(savedModel);
    }
    hasInitialModelSetRef.current = true;

    const savedAspectRatio = localStorage.getItem('sora_generator_aspectRatio');
    if (savedAspectRatio) setAspectRatio(savedAspectRatio);

    const savedResolution = localStorage.getItem('sora_generator_resolution');
    if (savedResolution) setResolution(savedResolution);

    const savedDuration = localStorage.getItem('sora_generator_duration');
    if (savedDuration) setDuration(savedDuration);

    const savedVariations = localStorage.getItem('sora_generator_variations');
    if (savedVariations) setVariations(savedVariations);

    const savedGrokMode = localStorage.getItem('sora_generator_mode');
    if (savedGrokMode) setMode(savedGrokMode);

    const savedFormat = localStorage.getItem('sora_generator_outputFormat');
    if (savedFormat) setOutputFormat(savedFormat);

    const savedImage = localStorage.getItem('sora_generator_image');
    if (savedImage) setSelectedImage(savedImage);

    const savedImages = localStorage.getItem('sora_generator_images');
    if (savedImages) {
      try {
        setSelectedImages(JSON.parse(savedImages));
      } catch (e) {}
    }

    const savedAutoSwitch = localStorage.getItem('sora_generator_auto_switch');
    if (savedAutoSwitch !== null) setAutoSwitchEnabled(savedAutoSwitch === 'true');
  }, [defaultModel]);

  useEffect(() => {
    localStorage.setItem('sora_generator_model', model);
    localStorage.setItem('sora_generator_aspectRatio', aspectRatio);
    localStorage.setItem('sora_generator_resolution', resolution);
    localStorage.setItem('sora_generator_duration', duration);
    localStorage.setItem('sora_generator_variations', variations);
    localStorage.setItem('sora_generator_mode', mode);
    localStorage.setItem('sora_generator_outputFormat', outputFormat);

    if (selectedImage) {
      localStorage.setItem('sora_generator_image', selectedImage);
    } else {
      localStorage.removeItem('sora_generator_image');
    }

    if (selectedImages.length > 0) {
      localStorage.setItem('sora_generator_images', JSON.stringify(selectedImages));
    } else {
      localStorage.removeItem('sora_generator_images');
    }

    localStorage.setItem('sora_generator_auto_switch', autoSwitchEnabled.toString());
  }, [
    model,
    aspectRatio,
    resolution,
    duration,
    variations,
    mode,
    outputFormat,
    selectedImage,
    selectedImages,
    autoSwitchEnabled,
  ]);

  const toggleDropdown = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    setExpandedSection(null);
  };

  const selectOption = useCallback(
    (dropdown: string, value: string, text: string) => {
      if (dropdown === 'model' && value === 'auto-switch') {
        setAutoSwitchEnabled((prev) => !prev);
        return;
      }
      switch (dropdown) {
        case 'model': {
          // 1. Save current settings for the OLD model
          const currentSettings = {
            aspectRatio,
            resolution,
            duration,
            variations,
            mode,
            outputFormat,
          };
          localStorage.setItem(`model_settings_${model}`, JSON.stringify(currentSettings));

          // 2. Load settings for the NEW model or use defaults
          const savedModelSettings = localStorage.getItem(`model_settings_${value}`);
          const config = MODEL_CONFIG[value];
          if (!config) {
            setModel(value);
            break;
          }

          if (savedModelSettings) {
            try {
              const settings = JSON.parse(savedModelSettings);

              // Validate and set Aspect Ratio
              if (config.ratios.includes(settings.aspectRatio)) {
                setAspectRatio(settings.aspectRatio);
              } else {
                setAspectRatio(config.defaultRatio);
              }

              // Validate and set Resolution
              if (config.resolutions.includes(settings.resolution)) {
                setResolution(settings.resolution);
              } else {
                setResolution(config.defaultResolution);
              }

              // Validate and set Duration
              if (config.durations.includes(settings.duration)) {
                setDuration(settings.duration);
              } else {
                setDuration(config.defaultDuration);
              }

              // Validate and set Variations
              setVariations(settings.variations || '1');
              setMode(settings.grokMode || settings.mode || 'normal');

              // Validate and set Output Format
              if (
                config.outputFormats &&
                settings.outputFormat &&
                config.outputFormats.includes(settings.outputFormat)
              ) {
                setOutputFormat(settings.outputFormat);
              } else {
                setOutputFormat(config.defaultFormat || 'jpg');
              }

              // Special validation for Runway: 1080p 10s is not supported
              if (value === 'runway') {
                if (settings.resolution === '1080p' && settings.duration === '10s') {
                  // Default to 1080p 5s if requested 1080p
                  setDuration('5s');
                  setResolution('1080p');
                }
              }
            } catch (e) {
              console.error('Failed to parse saved model settings', e);
              // Fallback to defaults
              setAspectRatio(config.defaultRatio);
              setResolution(config.defaultResolution);
              setDuration(config.defaultDuration);
              setOutputFormat(config.defaultFormat || 'jpg');
            }
          } else {
            // Apply hard defaults from config if no saved settings
            setAspectRatio(config.defaultRatio);
            setResolution(config.defaultResolution);
            setDuration(config.defaultDuration);
            setOutputFormat(config.defaultFormat || 'jpg');
          }

          setModel(value);
          break;
        }
        case 'outputFormat':
          setOutputFormat(value);
          setActiveDropdown(null);
        case 'mode':
          setMode(value);
          setActiveDropdown(null);
          break;
        case 'aspectRatio':
          setAspectRatio(text);
          break;
        case 'resolution':
          if (value === '1080p' && model === 'sora-2') {
            setModel('sora-2-pro');
          }
          if (value === '1080p' && model === 'runway') {
            setDuration('5s');
          }
          setResolution(value);
          break;
        case 'duration':
          if (text === '10s' && model === 'runway' && resolution === '1080p') {
            setResolution('720p');
          }
          setDuration(value);
          break;
        case 'variations':
          setVariations(text);
          break;
        case 'presets':
          setContentInput((prev) => {
            let newPrompt = prev;
            Object.values(PRESET_PROMPTS).forEach((presetText) => {
              if (newPrompt.includes(presetText)) {
                newPrompt = newPrompt.replace(presetText, '').trim();
              }
            });
            if (value !== 'none' && PRESET_PROMPTS[value]) {
              newPrompt = newPrompt
                ? `${newPrompt}\n\n${PRESET_PROMPTS[value]}`
                : PRESET_PROMPTS[value];
            }
            return newPrompt;
          });
          break;
        case 'camera':
          setContentInput((prev) => {
            let promptWithCamera = prev;
            Object.values(CAMERA_OPTIONS).forEach((optText) => {
              if (promptWithCamera.includes(optText)) {
                promptWithCamera = promptWithCamera.replace(optText, '').trim();
              }
            });
            if (value && CAMERA_OPTIONS[value]) {
              promptWithCamera = promptWithCamera
                ? `${promptWithCamera}\n\n${CAMERA_OPTIONS[value]}`
                : CAMERA_OPTIONS[value];
            }
            return promptWithCamera;
          });
          break;
        case 'lighting':
          setContentInput((prev) => {
            let promptWithLighting = prev;
            Object.values(LIGHTING_OPTIONS).forEach((optText) => {
              if (promptWithLighting.includes(optText)) {
                promptWithLighting = promptWithLighting.replace(optText, '').trim();
              }
            });
            if (value && LIGHTING_OPTIONS[value]) {
              promptWithLighting = promptWithLighting
                ? `${promptWithLighting}\n\n${LIGHTING_OPTIONS[value]}`
                : LIGHTING_OPTIONS[value];
            }
            return promptWithLighting;
          });
          break;
        case 'motion':
          setContentInput((prev) => {
            let promptWithMotion = prev;
            Object.values(MOTION_OPTIONS).forEach((optText) => {
              if (promptWithMotion.includes(optText)) {
                promptWithMotion = promptWithMotion.replace(optText, '').trim();
              }
            });
            if (value && MOTION_OPTIONS[value]) {
              promptWithMotion = promptWithMotion
                ? `${promptWithMotion}\n\n${MOTION_OPTIONS[value]}`
                : MOTION_OPTIONS[value];
            }
            return promptWithMotion;
          });
          break;
        case 'audio':
          setContentInput((prev) => {
            let promptWithAudio = prev;
            Object.values(AUDIO_OPTIONS).forEach((optText) => {
              if (promptWithAudio.includes(optText)) {
                promptWithAudio = promptWithAudio.replace(optText, '').trim();
              }
            });
            if (value && AUDIO_OPTIONS[value]) {
              promptWithAudio = promptWithAudio
                ? `${promptWithAudio}\n\n${AUDIO_OPTIONS[value]}`
                : AUDIO_OPTIONS[value];
            }
            return promptWithAudio;
          });
          break;
        case 'dialogue':
          setContentInput((prev) => {
            let promptWithDialogue = prev;
            Object.values(DIALOGUE_OPTIONS).forEach((optText) => {
              if (promptWithDialogue.includes(optText)) {
                promptWithDialogue = promptWithDialogue.replace(optText, '').trim();
              }
            });
            if (value && DIALOGUE_OPTIONS[value]) {
              promptWithDialogue = promptWithDialogue
                ? `${promptWithDialogue}\n\n${DIALOGUE_OPTIONS[value]}`
                : DIALOGUE_OPTIONS[value];
            }
            return promptWithDialogue;
          });
          break;
        case 'graphics':
          setContentInput((prev) => {
            let promptWithGraphics = prev;
            Object.values(GRAPHICS_OPTIONS).forEach((optText) => {
              if (promptWithGraphics.includes(optText)) {
                promptWithGraphics = promptWithGraphics.replace(optText, '').trim();
              }
            });
            if (value && GRAPHICS_OPTIONS[value]) {
              promptWithGraphics = promptWithGraphics
                ? `${promptWithGraphics}\n\n${GRAPHICS_OPTIONS[value]}`
                : GRAPHICS_OPTIONS[value];
            }
            return promptWithGraphics;
          });
          break;
        case 'image-style':
          setContentInput((prev) => {
            let promptWithImageStyle = prev;
            Object.values(IMAGE_STYLE_OPTIONS).forEach((optText) => {
              if (promptWithImageStyle.includes(optText)) {
                promptWithImageStyle = promptWithImageStyle.replace(optText, '').trim();
              }
            });
            if (value && IMAGE_STYLE_OPTIONS[value]) {
              promptWithImageStyle = promptWithImageStyle
                ? `${promptWithImageStyle}\n\n${IMAGE_STYLE_OPTIONS[value]}`
                : IMAGE_STYLE_OPTIONS[value];
            }
            return promptWithImageStyle;
          });
          break;
        case 'image-lighting':
          setContentInput((prev) => {
            let promptWithImageLighting = prev;
            Object.values(IMAGE_LIGHTING_OPTIONS).forEach((optText) => {
              if (promptWithImageLighting.includes(optText)) {
                promptWithImageLighting = promptWithImageLighting.replace(optText, '').trim();
              }
            });
            if (value && IMAGE_LIGHTING_OPTIONS[value]) {
              promptWithImageLighting = promptWithImageLighting
                ? `${promptWithImageLighting}\n\n${IMAGE_LIGHTING_OPTIONS[value]}`
                : IMAGE_LIGHTING_OPTIONS[value];
            }
            return promptWithImageLighting;
          });
          break;
        case 'image-composition':
          setContentInput((prev) => {
            let promptWithImageComposition = prev;
            Object.values(IMAGE_COMPOSITION_OPTIONS).forEach((optText) => {
              if (promptWithImageComposition.includes(optText)) {
                promptWithImageComposition = promptWithImageComposition.replace(optText, '').trim();
              }
            });
            if (value && IMAGE_COMPOSITION_OPTIONS[value]) {
              promptWithImageComposition = promptWithImageComposition
                ? `${promptWithImageComposition}\n\n${IMAGE_COMPOSITION_OPTIONS[value]}`
                : IMAGE_COMPOSITION_OPTIONS[value];
            }
            return promptWithImageComposition;
          });
          break;
        case 'image-mood':
          setContentInput((prev) => {
            let promptWithImageMood = prev;
            Object.values(IMAGE_MOOD_OPTIONS).forEach((optText) => {
              if (promptWithImageMood.includes(optText)) {
                promptWithImageMood = promptWithImageMood.replace(optText, '').trim();
              }
            });
            if (value && IMAGE_MOOD_OPTIONS[value]) {
              promptWithImageMood = promptWithImageMood
                ? `${promptWithImageMood}\n\n${IMAGE_MOOD_OPTIONS[value]}`
                : IMAGE_MOOD_OPTIONS[value];
            }
            return promptWithImageMood;
          });
          break;
      }

      if (
        ['model', 'aspectRatio', 'resolution', 'duration', 'variations', 'mode'].includes(dropdown)
      ) {
        setActiveDropdown(null);
      }
    },
    [aspectRatio, duration, model, resolution, variations, mode, outputFormat, setContentInput]
  );

  return {
    model,
    setModel,
    aspectRatio,
    setAspectRatio,
    resolution,
    setResolution,
    duration,
    setDuration,
    variations,
    setVariations,
    mode,
    setMode,
    outputFormat,
    setOutputFormat,
    selectedImage,
    setSelectedImage,
    selectedImages,
    setSelectedImages,
    activeDropdown,
    setActiveDropdown,
    isImageModalOpen,
    setIsImageModalOpen,
    expandedSection,
    setExpandedSection,
    toggleDropdown,
    selectOption,
    autoSwitchEnabled,
    setAutoSwitchEnabled,
  };
}
